import { useEffect } from "react";

const SITE_NAME = "All WHUFC";
const DEFAULT_DESCRIPTION = "The ultimate West Ham United fan experience. News, fixtures, stats, player ratings, chants, history, fan clubs, forums and more for Hammers fans worldwide.";
const DEFAULT_IMAGE = "https://allwhufc.com/og-image.png";
const BASE_URL = "https://allwhufc.com";

// Absolutize any URL for `og:image`, `twitter:image`, and URLs inside
// JSON-LD payloads. Relative paths (e.g. `/api/files/...`) shipped straight
// into a Googlebot-visible og:image are treated as invalid by Google's
// structured-data linter and lower quality signal — plus they break social
// share cards. Idempotent on already-absolute URLs.
function absolutize(u) {
  if (!u || typeof u !== "string") return u;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("//")) return "https:" + u;
  if (u.startsWith("/")) return BASE_URL + u;
  return `${BASE_URL}/${u}`;
}

// Recursively rewrite well-known URL-shaped keys inside a JSON-LD payload
// so downstream consumers (Google, Bing, social cards) never see a relative
// href / image path. This walks common Schema.org URL keys only — it is
// intentionally conservative to avoid mutating unrelated string fields.
const URL_KEYS = new Set([
  "url", "image", "logo", "sameAs", "mainEntityOfPage", "contentUrl", "thumbnailUrl",
]);
function absolutizeJsonLd(node) {
  if (node == null) return node;
  if (Array.isArray(node)) return node.map(absolutizeJsonLd);
  if (typeof node === "object") {
    const out = {};
    for (const [k, v] of Object.entries(node)) {
      if (URL_KEYS.has(k)) {
        if (Array.isArray(v)) out[k] = v.map(absolutize);
        else if (typeof v === "string") out[k] = absolutize(v);
        else out[k] = absolutizeJsonLd(v);
      } else if (typeof v === "object" && v !== null) {
        out[k] = absolutizeJsonLd(v);
      } else {
        out[k] = v;
      }
    }
    return out;
  }
  return node;
}

function setMeta(attr, attrVal, content) {
  let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Replace the entire JSON-LD block set with a single CSR-owned one. The
// build-time shell (postbuild-seo.js) already injects one JSON-LD script
// per route without any marker attribute. If we simply appended, the DOM
// would end up with TWO mutually-contradicting entities on the same page,
// weakening structured-data quality. Instead we wipe every existing
// application/ld+json script and replace with exactly one CSR-owned tagged
// script — the CSR payload is authoritative once the app has hydrated.
function setJsonLd(data) {
  document.querySelectorAll('script[type="application/ld+json"]').forEach((s) => s.remove());
  const el = document.createElement("script");
  el.setAttribute("type", "application/ld+json");
  el.setAttribute("data-seo-jsonld", "true");
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

// Remove only the CSR-owned block. If the caller didn't provide a jsonLd
// prop, the build-time JSON-LD (e.g. CollectionPage on hub pages, injected
// by postbuild-seo.js) must be preserved — otherwise we'd wipe a
// hand-curated schema for no reason on every hub-page navigation.
function removeJsonLd() {
  document
    .querySelectorAll('script[type="application/ld+json"][data-seo-jsonld="true"]')
    .forEach((s) => s.remove());
}

export const SEO = ({
  title,
  description,
  path = "",
  image,
  imageAlt,
  keywords,
  type = "website",
  noindex = false,
  jsonLd,
  seoTitle,      // full-title override — DB-authored `seo_title`
  seoDescription // meta-description override — DB-authored `seo_description`
}) => {
  // If the caller passes a DB-authored seo_title, use it verbatim (no brand
  // suffix appended — the LLM prompt requires a valid <title> tag by itself).
  // Otherwise fall back to `${title} | ${SITE_NAME}`.
  const fullTitle = seoTitle
    ? seoTitle
    : title
      ? `${title} | ${SITE_NAME}`
      : `${SITE_NAME} | The Ultimate West Ham Fan Experience`;
  const desc = seoDescription || description || DEFAULT_DESCRIPTION;
  const url = `${BASE_URL}${path}`;
  const img = absolutize(image) || DEFAULT_IMAGE;
  const kwStr = typeof keywords === "string" ? keywords.trim() : "";
  const altStr = typeof imageAlt === "string" ? imageAlt.trim() : "";

  // Callers pass `jsonLd` as an inline object literal on every render, so
  // object-identity comparison would refire the effect on every re-render.
  // Stringify to a stable key so useEffect only re-runs when the *content*
  // actually changes.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = fullTitle;

    setMeta("name", "description", desc);
    setLink("canonical", url);

    // <meta name="keywords"> — Google itself downplays this tag, but Bing,
    // Yandex, DuckDuckGo and many niche verticals (fan sports directories)
    // still read it. Skipping = 0 cost. Only write when we actually have
    // per-page terms; never blast the site default.
    if (kwStr) {
      setMeta("name", "keywords", kwStr);
    } else {
      const kwEl = document.querySelector('meta[name="keywords"]');
      if (kwEl) kwEl.remove();
    }

    if (noindex) {
      // Accept either a boolean (legacy: `noindex,nofollow`) or a specific
      // string (e.g. `"noindex,follow"` for pages where the link graph
      // should still be crawled — thin OTD days, empty leaderboards, etc.).
      const robotsValue = typeof noindex === "string" ? noindex : "noindex,nofollow";
      setMeta("name", "robots", robotsValue);
    } else {
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.remove();
    }

    setMeta("property", "og:type", type);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("property", "og:site_name", SITE_NAME);
    // og:image:alt improves both accessibility and social-card previews.
    if (altStr) {
      setMeta("property", "og:image:alt", altStr);
      setMeta("name", "twitter:image:alt", altStr);
    }

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);

    if (jsonLd) {
      setJsonLd(absolutizeJsonLd(jsonLd));
    } else {
      removeJsonLd();
    }
  }, [fullTitle, desc, url, img, type, noindex, jsonLdKey, kwStr, altStr]);

  return null;
};
